import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";

import { dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import locale from "date-fns/locale/en-US";

import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import EventIcon from "@material-ui/icons/Event";
import EventNoteIcon from "@material-ui/icons/EventNote";
import CancelIcon from "@material-ui/icons/Cancel";

import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";

import { setToggleEventModal } from "../../../../../participant/actions/bucketActions";

import getGuestAvatarConfig from "../../../../helpers/getGuestAvatarConfig";

import { isEventConfirmedByGuest } from "../../../../../participant/components/participantsEvents/utils";

import useStyles from "./useStyles";

const locales = {
  "en-US": locale,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const isOneOnOne = (eventDetails) => eventDetails?.eventType?.eventTypeId === 1;

const getDateLabel = (date) => {
  const formattedDate = localizer.format(date, "LLL dd,yyy");
  const isToday = localizer.format(new Date(), "LLL dd,yyy") === formattedDate;
  return `${isToday ? "Today " : ""}${formattedDate}`;
};

const isEventForConfirm = ({ confirmedGuest, eventType }) =>
  eventType.eventTypeId !== 1 && confirmedGuest === 0;

const wasEventRejected = ({ description }) =>
  ["Rejected"].includes(description);

const isEventForConfirmByEducator = ({
  confirmedGuest,
  eventType,
  description,
}) =>
  eventType.eventTypeId === 1 &&
  confirmedGuest === 0 &&
  !wasEventRejected({ description });

const getCustomStyles = (Event) => {
  const { eventStatus, confirmedGuest, eventType } = Event.resource;
  let { description } = eventStatus;
  if (
    isEventForConfirm({ confirmedGuest, eventType }) &&
    eventStatus.description === "Scheduled"
  ) {
    description = "ToConfirm";
  } else if (
    isEventForConfirmByEducator({ confirmedGuest, eventType, description })
  ) {
    description = "ToConfirmByEducator";
  }
  return description;
};

const UpcomingEventCard = ({ event }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { educatorName, profileInformation } = useSelector((state) => {
    return {
      educatorName: state.profile.educatorName,
      profileInformation: state.profile,
    };
  });

  const { guestAvatarProps, guestAvatarName } = getGuestAvatarConfig({
    educatorName,
    eventDetails: event.resource,
    participantName: profileInformation?.name,
    participantProfilePicturePath: profileInformation?.profilePicturePath,
  });

  const isEventForConfirmByParticipant = isEventForConfirm(event.resource);
  const wasEventConfirmedByGuest = isEventConfirmedByGuest(event.resource);
  const isEventRejected = wasEventRejected(event.resource?.eventStatus);
  const isOneOnOneRejected = isOneOnOne(event.resource) && isEventRejected;

  const getBadgeIcon = () => {
    if (!isEventForConfirmByParticipant && wasEventConfirmedByGuest) {
      return (
        <CheckCircleIcon
          color="primary"
          className={[classes.smallAvatarIcon, classes.confirmed].join(" ")}
        />
      );
    }

    if (isEventRejected) {
      return (
        <CancelIcon
          color="error"
          className={[classes.smallAvatarIcon, classes.rejected].join(" ")}
        />
      );
    }

    return null;
  };

  return (
    <Card
      onClick={() => {
        const { eventId } = event;
        dispatch(setToggleEventModal({ eventId }));
      }}
      className={[classes.eventRoot, getCustomStyles(event)].join(" ")}
    >
      <CardContent>
        <Typography
          className={[classes.tTruncate, classes.eventTitle].join(" ")}
          variant="subtitle2"
        >
          {event.title}
        </Typography>
        <Typography
          variant="body2"
          component="p"
          className={classes.eventDetails}
        >
          <span className={classes.eventDetail}>
            <EventIcon className={classes.eventDetailIcon} />
            &nbsp;
            {getDateLabel(event.start)}
          </span>
          <span className={classes.eventDetail}>
            <AccessTimeIcon className={classes.eventDetailIcon} />
            &nbsp;
            {localizer.format(event.start, "p")}
          </span>
          <span className={classes.eventDetail}>
            <EventNoteIcon className={classes.eventDetailIcon} />
            &nbsp;
            {event.resource.eventType.description}
          </span>
        </Typography>
        <Typography
          variant="caption"
          className={clsx(classes.guestBadge, classes.eventDetail)}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={getBadgeIcon()}
            className={classes.educatorAvatar}
          >
            <Avatar {...guestAvatarProps}>{guestAvatarName[0]}</Avatar>
          </Badge>
          {guestAvatarName}
        </Typography>
        {isOneOnOneRejected && (
          <Grid item xs={12} className={classes.rejectedWarningSection}>
            <Typography
              variant="caption"
              color="error"
              className={classes.rejectedEventErrorText}
            >
              <b>{guestAvatarName}</b> is unable to attend, please reschedule to
              a different time.
            </Typography>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

UpcomingEventCard.propTypes = {
  event: PropTypes.shape({
    eventId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
      .isRequired,
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
      .isRequired,
    resource: PropTypes.shape({
      eventType: PropTypes.shape({
        eventTypeId: PropTypes.number.isRequired,
        description: PropTypes.string.isRequired,
      }).isRequired,
      eventStatus: PropTypes.shape({
        description: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

export default UpcomingEventCard;
